import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import AdminLayout from "./AdminLayout";
import { getPricing, savePricing } from "../../firebase/pricingService";
import { FRAME_TYPES, SIZES, DEFAULT_PRICING } from "../../firebase/constants";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageHeader = styled.div`
  margin-bottom: 12px;
  margin-top: 3em;
`;

const PageTitle = styled.h1`
  color: #fff;
  font-size: 2rem;
  font-weight: 400;
  letter-spacing: 0.05em;
  margin: 0 0 4px 0;
`;

const PageSub = styled.p`
  color: #444;
  font-size: 0.72rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  margin: 0 0 12px 0;
`;

const Notice = styled.p`
  color: #555;
  font-size: 0.78rem;
  letter-spacing: 0.04em;
  line-height: 1.6;
  margin: 0 0 40px 0;
  border-left: 2px solid #222;
  padding-left: 16px;
`;

const Card = styled.div`
  background: #111;
  border: 1px solid #1e1e1e;
  padding: 36px;
  animation: ${fadeIn} 0.4s ease forwards;
  max-width: 820px;
`;

const SectionLabel = styled.p`
  color: #555;
  font-size: 0.65rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  margin: 0 0 20px 0;
`;

// ─── Pricing Table ────────────────────────────────────────────────────────────
const PricingTable = styled.div`
  border: 1px solid #1e1e1e;
  overflow: hidden;
  margin-bottom: 32px;
`;

const PricingHeader = styled.div`
  display: grid;
  grid-template-columns: 180px repeat(${() => SIZES.length}, 1fr);
  background: #0a0a0a;
  border-bottom: 1px solid #1e1e1e;
`;

const PricingHeaderCell = styled.div`
  padding: 12px 14px;
  color: #444;
  font-size: 0.62rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  border-right: 1px solid #1e1e1e;
  text-align: center;

  &:first-child {
    text-align: left;
  }
  &:last-child {
    border-right: none;
  }
`;

const PricingRow = styled.div`
  display: grid;
  grid-template-columns: 180px repeat(${() => SIZES.length}, 1fr);
  border-bottom: 1px solid #1e1e1e;

  &:last-child {
    border-bottom: none;
  }
`;

const PricingRowLabel = styled.div`
  padding: 0 14px;
  color: #888;
  font-size: 0.75rem;
  letter-spacing: 0.06em;
  border-right: 1px solid #1e1e1e;
  display: flex;
  align-items: center;
`;

const PricingCell = styled.div`
  border-right: 1px solid #1e1e1e;
  position: relative;

  &:last-child {
    border-right: none;
  }

  &:focus-within {
    background: #161616;
  }
`;

const CurrencySymbol = styled.span`
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #444;
  font-size: 0.75rem;
  pointer-events: none;
`;

const PricingInput = styled.input`
  width: 100%;
  height: 48px;
  background: transparent;
  border: none;
  color: #ddd;
  padding: 0 10px 0 24px;
  font-size: 0.88rem;
  font-family: "Georgia", serif;
  outline: none;
  box-sizing: border-box;
  text-align: center;

  &::placeholder {
    color: #2a2a2a;
  }

  /* Remove number arrows */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  -moz-appearance: textfield;
`;

// ─── Footer ───────────────────────────────────────────────────────────────────
const Footer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SaveBtn = styled.button`
  background: #fff;
  color: #0a0a0a;
  border: none;
  padding: 13px 32px;
  font-size: 0.72rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  cursor: pointer;
  font-family: "Georgia", serif;
  transition: background 0.2s;

  &:hover {
    background: #ccc;
  }
  &:disabled {
    background: #333;
    color: #666;
    cursor: not-allowed;
  }
`;

const StatusMsg = styled.span`
  font-size: 0.72rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${(p) =>
    p.$success ? "#2ecc71" : p.$error ? "#c0392b" : "transparent"};
`;

const LastSaved = styled.p`
  color: #333;
  font-size: 0.65rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin: 0 0 0 auto;
`;

// ─── Deep clone helper ────────────────────────────────────────────────────────
const deepClone = (obj) => JSON.parse(JSON.stringify(obj));

// ─── Component ────────────────────────────────────────────────────────────────
const AdminPrices = () => {
  const [pricing, setPricing] = useState(deepClone(DEFAULT_PRICING));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null); // "saved" | "error"
  const [lastSaved, setLastSaved] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await getPricing();
        if (data) setPricing(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleChange = (frameType, size, value) => {
    setPricing((prev) => ({
      ...prev,
      [frameType]: {
        ...prev[frameType],
        [size]: value === "" ? 0 : parseFloat(value) || 0,
      },
    }));
    setStatus(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setStatus(null);
    try {
      await savePricing(pricing);
      setStatus("saved");
      setLastSaved(new Date().toLocaleTimeString());
    } catch (err) {
      console.error(err);
      setStatus("error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout>
      <PageHeader>
        <PageTitle>Pricing</PageTitle>
        <PageSub>Global price table</PageSub>
      </PageHeader>

      <Notice>
        These prices apply to every product in the store. When a customer
        selects a frame type and size on any product page, the price shown comes
        from this table. Set each combination once and hit Save.
      </Notice>

      <Card>
        <SectionLabel>Price per frame type &amp; size — in €</SectionLabel>

        {loading ? (
          <p
            style={{
              color: "#444",
              fontSize: "0.8rem",
              letterSpacing: "0.1em",
              textTransform: "uppercase",
            }}
          >
            Loading...
          </p>
        ) : (
          <PricingTable>
            <PricingHeader>
              <PricingHeaderCell>Frame Type</PricingHeaderCell>
              {SIZES.map((s) => (
                <PricingHeaderCell key={s}>{s}</PricingHeaderCell>
              ))}
            </PricingHeader>

            {FRAME_TYPES.map((frame) => (
              <PricingRow key={frame}>
                <PricingRowLabel>{frame}</PricingRowLabel>
                {SIZES.map((size) => (
                  <PricingCell key={size}>
                    <CurrencySymbol>€</CurrencySymbol>
                    <PricingInput
                      type="number"
                      min="0"
                      placeholder="0"
                      value={pricing[frame]?.[size] || ""}
                      onChange={(e) =>
                        handleChange(frame, size, e.target.value)
                      }
                    />
                  </PricingCell>
                ))}
              </PricingRow>
            ))}
          </PricingTable>
        )}

        <Footer>
          <SaveBtn onClick={handleSave} disabled={saving || loading}>
            {saving ? "Saving..." : "Save Prices"}
          </SaveBtn>
          <StatusMsg $success={status === "saved"} $error={status === "error"}>
            {status === "saved"
              ? "✓ Prices saved"
              : status === "error"
                ? "Something went wrong"
                : ""}
          </StatusMsg>
          {lastSaved && <LastSaved>Last saved at {lastSaved}</LastSaved>}
        </Footer>
      </Card>
    </AdminLayout>
  );
};

export default AdminPrices;
